import './index.css';

import type { LexicalEditor, LexicalNode } from 'lexical';
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_NORMAL,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  KEY_MODIFIER_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
  $isRootOrShadowRoot,
  $getRoot,
  $insertNodes,
  $getNodeByKey,
} from 'lexical';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isDecoratorBlockNode } from '@lexical/react/LexicalDecoratorBlockNode';
import {
  $createHeadingNode,
  $isHeadingNode,
  HeadingTagType,
} from '@lexical/rich-text';
import { $selectAll, $wrapNodes, $setBlocksType } from '@lexical/selection';
import {
  $getNearestBlockElementAncestorOrThrow,
  $getNearestNodeOfType,
  mergeRegister,
  $findMatchingParent,
} from '@lexical/utils';
import { Dispatch, useCallback, useEffect, useState } from 'react';
import { useCommonSelector } from '@wajeez-web/stores/common';
import { IoIosLink } from 'react-icons/io';
import {
  MdFormatClear,
  MdFormatListBulleted,
  MdFormatListNumbered,
  MdKeyboardArrowDown,
} from 'react-icons/md';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { Dropdown } from '@wajeez-web/components/mui';
import {
  AiOutlineBold,
  AiOutlineItalic,
  AiOutlineRedo,
  AiOutlineUndo,
} from 'react-icons/ai';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';

import { sanitizeUrl } from '../utils/url';
import { getSelectedNode } from '../utils/getSelectedNode';

const blockTypeToBlockName = {
  bullet: 'Bulleted List',
  check: 'Check List',
  code: 'Code Block',
  h1: 'Heading 1',
  h2: 'Heading 2',
  h3: 'Heading 3',
  h4: 'Heading 4',
  h5: 'Heading 5',
  h6: 'Heading 6',
  number: 'Numbered List',
  paragraph: 'Paragraph',
  quote: 'Quote',
};

function BlockFormatDropDown({
  editor,
  blockType,
}: {
  blockType: keyof typeof blockTypeToBlockName;
  editor: LexicalEditor;
}): JSX.Element {
  const formatParagraph = () => {
    if (blockType !== 'paragraph') {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createParagraphNode());
        }
      });
    }
  };

  const formatHeading = (headingSize: HeadingTagType) => {
    if (blockType !== headingSize) {
      editor.update(() => {
        const selection = $getSelection();
        $setBlocksType(selection, () => $createHeadingNode(headingSize));
      });
    }
  };

  return (
    <Dropdown
      id="heading"
      label={
        <div className="w-[110px] flex justify-between items-center">
          <div>
            {blockType !== 'bullet' && blockType !== 'number'
              ? blockTypeToBlockName[blockType]
              : 'Paragraph'}
          </div>
          <MdKeyboardArrowDown />
        </div>
      }
      items={[
        { label: 'Paragraph', onClick: formatParagraph },
        { label: 'Heading 1', onClick: () => formatHeading('h1') },
        { label: 'Heading 2', onClick: () => formatHeading('h2') },
        { label: 'Heading 3', onClick: () => formatHeading('h3') },
      ]}
      buttonStyle={{
        border: '0px',
        color: 'currentcolor',
        height: '100%',
      }}
    />
  );
}

export const ToolbarPlugin = ({
  value,
  setIsLinkEditMode,
}: {
  value: string;
  setIsLinkEditMode: Dispatch<boolean>;
}) => {
  const [editor] = useLexicalComposerContext();
  const [activeEditor, setActiveEditor] = useState(editor);
  const [blockType, setBlockType] =
    useState<keyof typeof blockTypeToBlockName>('paragraph');
  const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const dir = useCommonSelector(({ common }) => common.settings.dir);

  const formatBulletList = useCallback(() => {
    if (blockType !== 'bullet') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  }, [blockType, editor]);

  const formatNumberedList = useCallback(() => {
    if (blockType !== 'number') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  }, [blockType, editor]);

  const updateToolbar = useCallback(async () => {
    try {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const anchorNode = selection.anchor.getNode();
        let element =
          anchorNode.getKey() === 'root'
            ? anchorNode
            : $findMatchingParent(anchorNode, (e) => {
                const parent = e.getParent();
                return parent !== null && $isRootOrShadowRoot(parent);
              });

        if (element === null) {
          element = anchorNode.getTopLevelElementOrThrow();
        }

        const elementKey = element.getKey();
        const elementDOM = activeEditor.getElementByKey(elementKey);

        // Update text format
        setIsBold(selection.hasFormat('bold'));
        setIsItalic(selection.hasFormat('italic'));

        // Update links
        const node = getSelectedNode(selection);
        const parent = node.getParent();
        if ($isLinkNode(parent) || $isLinkNode(node)) {
          setIsLink(true);
        } else {
          setIsLink(false);
        }

        if (elementDOM !== null) {
          if ($isListNode(element)) {
            const parentList = $getNearestNodeOfType<ListNode>(
              anchorNode,
              ListNode
            );
            const type = parentList
              ? parentList.getListType()
              : element.getListType();
            setBlockType(type);
          } else {
            const type = $isHeadingNode(element)
              ? element.getTag()
              : element.getType();
            if (type in blockTypeToBlockName) {
              setBlockType(type as keyof typeof blockTypeToBlockName);
            }
          }
        }
      }
    } catch (error) {
      console.log(error);
    }
  }, [activeEditor]);

  useEffect(() => {
    if (value) {
      editor.update(() => {
        const parser = new DOMParser();
        const dom = parser.parseFromString(
          '<span style="white-space: pre-wrap;"></span>' + value || '',
          'text/html'
        );

        // dom.body.

        console.log(value, dom);

        // Once you have the DOM instance it's easy to generate LexicalNodes.
        const nodes = $generateNodesFromDOM(editor, dom) as LexicalNode[];

        console.log(nodes);

        // Select the root
        $getRoot().select();

        // Insert them at a selection.
        $insertNodes(nodes);

        const firstNode = nodes[0];

        $getNodeByKey(firstNode.__prev as string)?.remove();
      });
    }

    return () => {
      if (value) {
        editor.update(() => {
          const selection = $getSelection();

          if ($isRangeSelection(selection)) {
            $selectAll(selection);

            selection.getNodes().forEach((node) => {
              node.remove();
            });
          }
        });
      }
    };
  }, []);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      (_payload, newEditor) => {
        updateToolbar();
        setActiveEditor(newEditor);
        return false;
      },
      COMMAND_PRIORITY_CRITICAL
    );
  }, [editor, updateToolbar]);

  useEffect(() => {
    return mergeRegister(
      activeEditor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      activeEditor.registerCommand<boolean>(
        CAN_UNDO_COMMAND,
        (payload) => {
          setCanUndo(payload);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL
      ),
      activeEditor.registerCommand<boolean>(
        CAN_REDO_COMMAND,
        (payload) => {
          setCanRedo(payload);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL
      )
    );
  }, [activeEditor, updateToolbar]);

  useEffect(() => {
    return activeEditor.registerCommand(
      KEY_MODIFIER_COMMAND,
      (payload) => {
        const event: KeyboardEvent = payload;
        const { code, ctrlKey, metaKey } = event;

        if (code === 'KeyK' && (ctrlKey || metaKey)) {
          event.preventDefault();
          let url: string | null;
          if (!isLink) {
            setIsLinkEditMode(true);
            url = sanitizeUrl('https://');
          } else {
            setIsLinkEditMode(false);
            url = null;
          }
          return activeEditor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
        }
        return false;
      },
      COMMAND_PRIORITY_NORMAL
    );
  }, [activeEditor, isLink, setIsLinkEditMode]);

  useEffect(() => {
    if (dir === 'rtl') {
      editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right');
    } else {
      editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
    }
  }, [dir]);

  const clearFormatting = useCallback(() => {
    activeEditor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $selectAll(selection);
        selection.getNodes().forEach((node) => {
          if ($isTextNode(node)) {
            node.setFormat(0);
            node.setStyle('');
            $getNearestBlockElementAncestorOrThrow(node).setFormat('');
          }
          if ($isDecoratorBlockNode(node)) {
            node.setFormat('');
          }
        });
      }
    });
  }, [activeEditor]);

  const insertLink = useCallback(() => {
    if (!isLink) {
      setIsLinkEditMode(true);
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizeUrl('https://'));
    } else {
      setIsLinkEditMode(false);
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, isLink]);

  return (
    <div className="toolbar">
      <button
        disabled={!canUndo}
        onClick={async () => {
          try {
            activeEditor.dispatchCommand(UNDO_COMMAND, undefined);
          } catch (error) {
            console.log(error);
          }
        }}
        title={'Undo (Ctrl+Z)'}
        className="toolbar-item spaced flex justify-center items-center text-[22px]"
        aria-label="Undo"
      >
        <AiOutlineUndo />
      </button>
      <button
        disabled={!canRedo}
        onClick={() => {
          activeEditor.dispatchCommand(REDO_COMMAND, undefined);
        }}
        title={'Undo (Ctrl+Y)'}
        className="toolbar-item flex justify-center items-center text-[22px]"
        aria-label="Redo"
      >
        <AiOutlineRedo />
      </button>
      <button
        onClick={formatBulletList}
        className={
          'toolbar-item spaced flex justify-center items-center text-[22px] ' +
          (blockType === 'bullet' ? 'active' : '')
        }
        title={'Bullet List'}
        aria-label={`Format text as bullet list`}
      >
        <MdFormatListBulleted />
      </button>
      <button
        onClick={formatNumberedList}
        className={
          'toolbar-item spaced flex justify-center items-center text-[22px] ' +
          (blockType === 'number' ? 'active' : '')
        }
        title={'Numbered List'}
        aria-label={`Format text as numbered list`}
      >
        <MdFormatListNumbered />
      </button>
      {blockType in blockTypeToBlockName && activeEditor === editor && (
        <BlockFormatDropDown blockType={blockType} editor={editor} />
      )}
      <button
        onClick={() => {
          activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
        }}
        className={
          'toolbar-item spaced flex justify-center items-center text-[22px] ' +
          (isBold ? 'active' : '')
        }
        title={'Bold (Ctrl+B)'}
        aria-label={`Format text as bold. Shortcut: ${'Ctrl+B'}`}
      >
        <AiOutlineBold />
      </button>
      <button
        onClick={() => {
          activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
        }}
        className={
          'toolbar-item spaced flex justify-center items-center text-[22px] ' +
          (isItalic ? 'active' : '')
        }
        title={'Italic (Ctrl+I)'}
        aria-label={`Format text as italics. Shortcut: ${'Ctrl+I'}`}
      >
        <AiOutlineItalic />
      </button>
      <button
        onClick={insertLink}
        className={
          'toolbar-item spaced flex justify-center items-center text-[22px] ' +
          (isLink ? 'active' : '')
        }
        aria-label="Insert link"
        title="Insert link"
      >
        <IoIosLink />
      </button>
      {/* {isLink && (
        <FloatingLinkEditor
          editor={activeEditor}
          isOpen={isLink}
          onClose={() => {
            setIsLink(false);
          }}
        />
      )} */}
      <button
        onClick={clearFormatting}
        className={
          'toolbar-item spaced flex justify-center items-center text-[22px]'
        }
        aria-label="Clear formatting"
        title="Clear formatting"
      >
        <MdFormatClear />
      </button>
    </div>
  );
};
